import { useEffect, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { TApi } from '../../../../shared/const'
import { getFormattedDate, stateLabel } from '../../../../shared/helpers'
import http from '../../../../shared/http'

import Loading from '../../../../UI/Loading'
import ReminderNote from '../../../../UI/ReminderNote'
import Card from './Card'

import styles from './editModal.module.scss'

/**
 * Reiter „Unternehmen & Bedarfe".
 *
 * Zwei Fragen, die die Verwaltung an einem Profil stellt und die bisher nur
 * verstreut zu beantworten waren: Welche Junior Companies betreut diese Person —
 * und was hat sie auf ausgeschriebene Unterstützungsbedarfe geantwortet? Die
 * Verbindungen standen bis hierher klein am Ende des Status-Reiters, die
 * Antworten auf Bedarfe waren vom Profil aus gar nicht zu finden: Man musste
 * jeden Bedarf einzeln öffnen und die Antwortliste durchsehen.
 */
export default function EngagementTab({ supporterId, connections }) {
	const { t } = useTranslation()

	const [requests, setRequests] = useState(null)

	useEffect(() => {
		if (!supporterId) return

		setRequests(null)
		http.get(TApi.ADMIN_SUPPORTER_SUPPORT_REQUESTS(supporterId))
			.then(({ data }) => setRequests(data))
			.catch(() => setRequests([])) // Meldung kommt aus shared/http
	}, [supporterId])

	const rows = connections || []

	/** Die Daten einer Verbindung als eine Zeile: angefragt, beantwortet, beendet. */
	const connectionDates = connection =>
		[
			connection.requestedAt && `${t('requestedOn')} ${getFormattedDate(connection.requestedAt)}`,
			connection.respondedAt && `${t('respondedOn')} ${getFormattedDate(connection.respondedAt)}`,
			connection.endedAt && `${t('endedOn')} ${getFormattedDate(connection.endedAt)}`
		]
			.filter(Boolean)
			.join(' · ')

	return (
		<>
			<Card title={t('connectedCompanies')} hint={t('connectedCompaniesHint')}>
				{!rows.length ? (
					<p className={styles.hint}>{t('noConnectedCompanies')}</p>
				) : (
					<ul className={styles.engageList}>
						{rows.map(connection => (
							<li key={connection.id} className={styles.engageRow}>
								<div className={styles.engageMain}>
									<span className={styles.engageTitle}>{connection.companyName || '—'}</span>
									<span className={styles.engageMeta}>
										{[
											stateLabel(connection.companyState),
											connectionDates(connection)
										]
											.filter(Boolean)
											.join(' · ')}
									</span>
									{connection.requestMessage && (
										<p className={styles.engageText}>{connection.requestMessage}</p>
									)}
									<ReminderNote
										status={connection.status}
										reminderCount={connection.reminderCount}
										lastReminderAt={connection.lastReminderAt}
										autoDeclined={connection.autoDeclined}
									/>
								</div>
								<span
									className={clsx(
										styles.connectionStatus,
										styles[`connectionStatus${connection.status}`]
									)}
								>
									{t(`connectionStatus${connection.status}`)}
								</span>
							</li>
						))}
					</ul>
				)}
			</Card>

			<Card title={t('supportRequestResponses')} hint={t('supportRequestResponsesHint')}>
				{requests === null && <Loading />}
				{requests?.length === 0 && <p className={styles.hint}>{t('noSupportRequestResponses')}</p>}
				{!!requests?.length && (
					<ul className={styles.engageList}>
						{requests.map(response => (
							<li key={response.id} className={styles.engageRow}>
								<div className={styles.engageMain}>
									<span className={styles.engageTitle}>{response.title || '—'}</span>
									<span className={styles.engageMeta}>
										{[
											response.category,
											response.eventDate &&
												`${t('eventDate')}: ${getFormattedDate(response.eventDate)}`,
											response.location,
											response.respondedAt &&
												`${t('respondedOn')} ${getFormattedDate(response.respondedAt)}`
										]
											.filter(Boolean)
											.join(' · ')}
									</span>
									{response.note && <p className={styles.engageText}>{response.note}</p>}
								</div>
								<span
									className={clsx(
										styles.connectionStatus,
										response.status === 'ACCEPTED'
											? styles.connectionStatusCONNECTED
											: styles.responseStatusDECLINED
									)}
								>
									{t(response.status === 'ACCEPTED' ? 'responseAccepted' : 'responseDeclined')}
								</span>
							</li>
						))}
					</ul>
				)}
			</Card>
		</>
	)
}

EngagementTab.propTypes = {
	supporterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	connections: PropTypes.array
}
